import { useAuth } from "@/contexts/AuthContext";
import { useProperties } from "@/contexts/PropertyContext";
import { Search, Moon, Sun, Menu } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HotelSwitcher from "@/components/HotelSwitcher";

export default function Topbar({ title, subtitle, actions }) {
    const { user } = useAuth();
    const { properties, activePropertyId, setActive } = useProperties();
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Dark-mode state — persist via localStorage so it survives full page reloads.
    const [dark, setDark] = useState(() => {
        try {
            const saved = localStorage.getItem("akm_theme");
            if (saved === "dark") return true;
            if (saved === "light") return false;
        } catch { /* ignore */ }
        return document.documentElement.classList.contains("dark");
    });
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [searchCoords, setSearchCoords] = useState({ top: 0, right: 0 });
    const boxRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        document.documentElement.classList.toggle("dark", dark);
        try { localStorage.setItem("akm_theme", dark ? "dark" : "light"); } catch { /* ignore */ }
    }, [dark]);

    // Debounced global search — hits /reservations, /guests, /rooms in parallel
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); setOpen(false); return; }
        const timer = setTimeout(async () => {
            try {
                const [resv, guests, rooms] = await Promise.all([
                    api.get("/reservations").catch(() => ({ data: [] })),
                    api.get("/guests").catch(() => ({ data: [] })),
                    api.get("/rooms").catch(() => ({ data: [] })),
                ]);
                const lower = q.toLowerCase();
                const hits = [];
                for (const r of (resv.data || []).slice(0, 200)) {
                    const name = `${r.guest_first_name || ""} ${r.guest_last_name || ""}`.trim();
                    if (name.toLowerCase().includes(lower) || (r.confirmation_number || "").toLowerCase().includes(lower)) {
                        hits.push({ kind: "res", label: name || "(pa emër)", meta: r.confirmation_number || `AK-${(r.id || "").slice(0,6).toUpperCase()}`, to: `/reservations?id=${r.id}` });
                        if (hits.length >= 6) break;
                    }
                }
                for (const g of (guests.data || []).slice(0, 200)) {
                    const name = `${g.first_name || ""} ${g.last_name || ""}`.trim();
                    if (name.toLowerCase().includes(lower) || (g.email || "").toLowerCase().includes(lower)) {
                        hits.push({ kind: "guest", label: name || g.email, meta: g.email || "", to: `/guests?id=${g.id}` });
                        if (hits.length >= 12) break;
                    }
                }
                for (const rm of (rooms.data || [])) {
                    if (String(rm.number).toLowerCase().includes(lower) || (rm.room_type || "").toLowerCase().includes(lower)) {
                        hits.push({ kind: "room", label: `${t("dashboard.col_room")} ${rm.number}`, meta: rm.room_type || "", to: `/rooms?id=${rm.id}` });
                        if (hits.length >= 16) break;
                    }
                }
                setResults(hits.slice(0, 8));
                setOpen(hits.length > 0);
            } catch {
                setResults([]); setOpen(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [query]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onClick = (e) => {
            if (!boxRef.current) return;
            if (boxRef.current.contains(e.target)) return;
            if (boxRef.current.__portal && boxRef.current.__portal.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Position the fixed dropdown right under the search input.
    useEffect(() => {
        if (!open) return;
        const update = () => {
            if (!inputRef.current) return;
            const r = inputRef.current.getBoundingClientRect();
            setSearchCoords({ top: r.bottom + 4, right: Math.max(6, window.innerWidth - r.right) });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    const pick = (r) => { setQuery(""); setOpen(false); navigate(r.to); };

    return (
        <header className="sticky top-0 z-20 flex h-14 flex-nowrap items-center gap-1.5 overflow-x-auto border-b border-border bg-card/95 backdrop-blur px-3 sm:gap-2 sm:px-4 [&>*]:shrink-0">
            {/* Mobile/tablet hamburger */}
            <button
                type="button"
                onClick={() => window.__akmToggleSidebar?.()}
                className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground lg:hidden"
                data-testid="topbar-menu-btn"
                aria-label="Menu"
            >
                <Menu size={20} />
            </button>

            <div className="min-w-0 !shrink flex-1">
                <h1 data-testid="topbar-title" className="truncate text-sm font-semibold tracking-tight sm:text-base">{title}</h1>
                {subtitle && <div className="hidden truncate text-xs text-muted-foreground sm:block">{subtitle}</div>}
            </div>

            {properties.length > 1 && user?.role !== "super_admin" && (
                <select
                    data-testid="topbar-property-switcher"
                    value={activePropertyId}
                    onChange={(e) => setActive(e.target.value)}
                    className="akm-input hidden h-8 w-[140px] text-[12px] sm:block sm:w-[180px] sm:text-[13px]"
                >
                    {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            )}

            <div ref={boxRef} className="relative hidden md:block">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                    ref={inputRef}
                    data-testid="topbar-search"
                    placeholder={t("topbar.search_placeholder")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") { setOpen(false); setQuery(""); }
                        else if (e.key === "Enter" && results[0]) pick(results[0]);
                    }}
                    className="akm-input h-8 w-[180px] pl-8 text-[13px] lg:w-[260px]"
                />
                {open && results.length > 0 && createPortal(
                    <div
                        data-testid="topbar-search-results"
                        className="fixed z-[80] w-[340px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-md border border-border bg-card shadow-lg"
                        style={{ top: searchCoords.top, right: searchCoords.right }}
                        ref={(el) => { if (boxRef.current) boxRef.current.__portal = el; }}
                    >
                        <ul className="max-h-[360px] overflow-y-auto py-1 text-[13px]">
                            {results.map((r, i) => (
                                <li key={`${r.kind}-${i}`}>
                                    <button
                                        onClick={() => pick(r)}
                                        data-testid={`topbar-search-hit-${r.kind}-${i}`}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/60"
                                    >
                                        <span className={`inline-flex h-5 min-w-[24px] items-center justify-center rounded px-1 text-[9.5px] font-bold uppercase ${
                                            r.kind === "res"   ? "bg-blue-100 text-blue-700" :
                                            r.kind === "guest" ? "bg-emerald-100 text-emerald-700" :
                                                                 "bg-violet-100 text-violet-700"
                                        }`}>{r.kind === "res" ? "R" : r.kind === "guest" ? "K" : "D"}</span>
                                        <span className="flex-1 truncate font-medium text-foreground">{r.label}</span>
                                        {r.meta && <span className="truncate text-[11.5px] text-muted-foreground">{r.meta}</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body
                )}
            </div>

            <button
                data-testid="topbar-theme-toggle"
                onClick={() => setDark((d) => !d)}
                className="rounded-md border border-border bg-card p-1.5 text-muted-foreground hover:text-foreground"
                title={t("topbar.toggle_theme")}
            >
                {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <LanguageSwitcher />
            <HotelSwitcher />
            <NotificationBell />

            {actions}
        </header>
    );
}
